import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS, SPACING } from '../styles/constants';
import ServiceCard from '../components/ServiceCard';
import VideoBackground from '../components/VideoBackground';

// Icons
import { FaTrain, FaLandmark, FaHospital, FaUniversity, FaHardHat, FaArrowUp } from 'react-icons/fa';

// Services data
import { services } from '../data/servicesData';

const HomePage = styled.div`
  color: ${COLORS.dark};
`;

const HeroSection = styled.div`
  height: 100vh;
  width: 100%;
  position: relative;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 1000px;
  padding: ${SPACING.xl} ${SPACING.md};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding-top: 150px;
    padding: 150px ${SPACING.sm} ${SPACING.xl};
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  color: white;
  margin-bottom: ${SPACING.sm};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.4rem;
  color: white;
  margin-bottom: ${SPACING.lg};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: ${SPACING.md};
  }
`;

const HeroButton = styled(Link)`
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  padding: ${SPACING.sm} ${SPACING.xl};
  border-radius: 4px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  display: inline-block;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #0090C0;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const Section = styled.section`
  padding: ${SPACING.lg} ${SPACING.md};
  max-width: 1100px;
  margin: 0 auto;
  
  &:not(:last-child) {
    margin-bottom: ${SPACING.xl};
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: ${SPACING.md};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background-color: ${COLORS.primary};
  }
`;

const SectionDescription = styled.p`
  text-align: center;
  max-width: 900px;
  margin: 0 auto ${SPACING.xl};
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${COLORS.dark};
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${SPACING.md};
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CenteredRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${SPACING.md};
  flex-wrap: wrap;
  
  & > * {
    flex: 0 0 calc(33.33% - ${SPACING.md});
    max-width: calc(33.33% - ${SPACING.md});
  }
  
  @media (max-width: 1024px) {
    & > * {
      flex: 0 0 calc(50% - ${SPACING.md});
      max-width: calc(50% - ${SPACING.md});
    }
  }
  
  @media (max-width: 768px) {
    & > * {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
`;

const AboutSection = styled.section`
  background: linear-gradient(135deg, #032a40 0%, #001824 100%);
  margin: 40px 0 0;
  padding: 80px 20px;
  position: relative;
  color: white;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, ${COLORS.primary}, #0072a8);
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const AboutContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const AboutTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: ${SPACING.lg};
  position: relative;
  color: white;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background-color: ${COLORS.primary};
  }
`;

const AboutContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 144, 192, 0.2) 0%, rgba(0, 144, 192, 0) 70%);
    top: -40px;
    left: -100px;
    border-radius: 50%;
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(0, 144, 192, 0.15) 0%, rgba(0, 144, 192, 0) 70%);
    bottom: -20px;
    right: -70px;
    border-radius: 50%;
    z-index: -1;
  }
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: ${SPACING.lg};
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${COLORS.primary};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.visible ? '1' : '0'};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;

  &:hover {
    background-color: #0090C0;
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
`;

const Home = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Industry data
  const industries = [
    {
      icon: <FaTrain size={20} />,
      title: "Rail Transportation"
    },
    {
      icon: <FaLandmark size={20} />,
      title: "Financial Services"
    },
    {
      icon: <FaHospital size={20} />,
      title: "Health Care"
    },
    {
      icon: <FaUniversity size={20} />,
      title: "Government Sector"
    },
    {
      icon: <FaHardHat size={20} />,
      title: "Construction"
    }
  ];

  const handleLearnMore = (path) => {
    navigate(path);
  };

  return (
    <HomePage>
      <HeroSection>
        <VideoBackground>
          <HeroContent>
            <HeroTitle>Welcome to JNAR</HeroTitle>
            <HeroSubtitle>
              Let us release the power of data and AI to drive innovation, uncover insights, and fuel informed decision-making
            </HeroSubtitle>
            <HeroButton to="/contact">Contact Us</HeroButton>
          </HeroContent>
        </VideoBackground>
      </HeroSection>
      
      <Section id="services">
        <SectionTitle>Proposed Service Offering</SectionTitle>
        <SectionDescription>
          We empower organizations with actionable insights through cutting-edge data analytics solutions, leveraging advanced technologies and expert-driven strategies to transform data into valuable business intelligence. We are committed to providing data-driven decision-making tools that drive efficiency, innovation, and growth, enabling our clients to stay ahead in an increasingly data-centric world.
        </SectionDescription>
        
        <GridContainer>
          <ServicesGrid>
            {services.slice(0, 6).map((service, index) => (
              <ServiceCard
                key={index}
                icon={service.icon}
                title={service.title}
                onLearnMore={() => handleLearnMore(service.path)}
              />
            ))}
          </ServicesGrid>
          
          <CenteredRow>
            {services.slice(6, 8).map((service, index) => (
              <ServiceCard
                key={`service-bottom-${index}`}
                icon={service.icon}
                title={service.title}
                onLearnMore={() => handleLearnMore(service.path)}
              />
            ))}
          </CenteredRow>
        </GridContainer>
      </Section>

      <Section id="industries">
        <SectionTitle>Industries</SectionTitle>
        <SectionDescription>
          JNAR has successfully delivered high value data transformation projects in various industries:
        </SectionDescription>
        
        <GridContainer>
          <ServicesGrid>
            {industries.slice(0, 3).map((industry, index) => (
              <ServiceCard
                key={`industry-${index}`}
                icon={industry.icon}
                title={industry.title}
                hideLearnMore={true}
              />
            ))}
          </ServicesGrid>
          
          <CenteredRow>
            {industries.slice(3, 5).map((industry, index) => (
              <ServiceCard
                key={`industry-bottom-${index}`}
                icon={industry.icon}
                title={industry.title}
                hideLearnMore={true}
              />
            ))}
          </CenteredRow>
        </GridContainer>
      </Section>

      <AboutSection id="about">
        <AboutContainer>
          <AboutTitle>About Us</AboutTitle>
          <AboutContent>
            <AboutText>
              At JNAR, we empower businesses with data-driven insights and cutting-edge analytics solutions. Our mission is to transform raw data into actionable intelligence, helping organizations make smarter decisions, optimize operations, and drive growth.
            </AboutText>
            <AboutText>
              With expertise in data management, predictive analytics, business intelligence, and AI-driven solutions, we provide customized services tailored to your unique needs. Whether it's data integration, visualization, machine learning, or real-time reporting, our team of experts ensures that you extract maximum value from your data.
            </AboutText>
            <AboutText>
              We believe that data should be an asset, not a challenge. That's why we focus on delivering scalable, secure, and innovative solutions that enhance efficiency and competitiveness. Partner with us and unlock the full potential of your data.
            </AboutText>
          </AboutContent>
        </AboutContainer>
      </AboutSection>

      <ScrollToTopButton 
        visible={showScrollButton} 
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <FaArrowUp size={20} />
      </ScrollToTopButton>
    </HomePage>
  );
};

export default Home;